import { useState, useEffect, useRef } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useAppStore } from "@/lib/store";
import { authGuard } from "@/lib/authGuard";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft, ArrowRight, Circle } from "lucide-react";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Tester Profile — PataTasks" },
      {
        name: "description",
        content: "Complete your tester profile to start accepting app and web tests.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const store = await authGuard();
    if (!store.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
    // Server is the source of truth. If the account has already completed
    // onboarding (on ANY device), skip straight past this route.
    if (store.user?.onboardingComplete) {
      throw redirect({ to: "/" });
    }
  },
  component: OnboardingPage,
});

const MULTI_SELECT_QUESTIONS = new Set(["q1", "q2", "q3"]);

const QUESTIONS = [
  {
    id: "q1",
    text: "Which devices do you currently have access to for testing?",
    multi: true,
    options: [
      "Android smartphone",
      "iPhone / iOS smartphone",
      "Android tablet",
      "iPad / iOS tablet",
      "Windows laptop or desktop",
      "Mac laptop or desktop",
      "Chromebook",
    ],
  },
  {
    id: "q2",
    text: "Which app stores can you actively install apps from?",
    multi: true,
    options: ["Google Play Store", "Apple App Store", "Both", "Neither : I test on web only"],
  },
  {
    id: "q3",
    text: "Which types of apps are you comfortable testing?",
    multi: true,
    options: [
      "Android mobile apps",
      "iOS mobile apps",
      "Windows desktop apps",
      "Mac desktop apps",
      "Web apps / websites",
      "Progressive web apps (PWAs)",
    ],
  },
  {
    id: "q4",
    text: "How many different mobile or web apps do you actively use per day?",
    multi: false,
    options: ["1–3", "4–7", "8–15", "15+"],
  },
  {
    id: "q5",
    text: "To confirm you are paying attention, select 'Moderate'",
    multi: false,
    options: ["Low", "Moderate", "High"],
  },
];

type Answers = Record<string, string | string[]>;

type FailedQuestion = {
  id: string;
  reason: string;
  expected?: string;
};

type ValidationResult = {
  passed: boolean;
  reasons: string[];
  failedQuestions: FailedQuestion[];
};

function validateAnswers(answers: Answers): ValidationResult {
  const reasons: string[] = [];
  const failedQuestions: FailedQuestion[] = [];

  if (answers["q5"] !== "Moderate") {
    reasons.push("You failed the attention check question.");
    failedQuestions.push({
      id: "q5",
      reason: "Expected answer: Moderate",
      expected: "Moderate",
    });
  }

  return {
    passed: reasons.length === 0,
    reasons,
    failedQuestions,
  };
}

// Auto-advance delay after a single-select answer, in ms
const AUTO_ADVANCE_DELAY = 300;

function OnboardingPage() {
  const navigate = useNavigate();
  const user = useAppStore((s) => s.user);
  const setUser = useAppStore((s) => s.setUser);
  const [answers, setAnswers] = useState<Answers>({});
  const [stage, setStage] = useState<"quiz" | "review" | "loading" | "success">("quiz");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failureReasons, setFailureReasons] = useState<string[]>([]);
  const [failedQuestions, setFailedQuestions] = useState<FailedQuestion[]>([]);
  const [step, setStep] = useState(0);
  const [analyzingStep, setAnalyzingStep] = useState(0);
  const analyzingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const analyzingSteps = [
    "Analyzing your responses...",
    "Checking focus and attention...",
    "Analyzing your profile...",
    "Analyzing your devices...",
  ];

  useEffect(() => {
    return () => {
      if (analyzingTimerRef.current) {
        clearInterval(analyzingTimerRef.current);
      }
    };
  }, []);
  // progress mid-quiz on a page refresh. It is NEVER used to decide whether
  // onboarding is "complete" — that decision is made exclusively by the
  // server (users.onboardingComplete), which is checked in beforeLoad above.
  useEffect(() => {
    const saved = localStorage.getItem("onboarding_answers");
    if (saved) {
      try {
        setAnswers(JSON.parse(saved));
      } catch {
        // ignore invalid stored data
      }
    }
  }, []);

  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem("onboarding_answers", JSON.stringify(answers));
    }
  }, [answers]);

  const currentQuestion = QUESTIONS[step];
  const totalQuestions = QUESTIONS.length;
  const answeredCount = QUESTIONS.filter((q) => {
    const answer = answers[q.id];
    if (MULTI_SELECT_QUESTIONS.has(q.id)) {
      return Array.isArray(answer) && answer.length > 0;
    }
    return typeof answer === "string" && answer.trim().length > 0;
  }).length;
  const progressPercent = totalQuestions > 0 ? ((step + 1) / totalQuestions) * 100 : 0;

  const isCurrentAnswered = () => {
    if (!currentQuestion) return false;
    const answer = answers[currentQuestion.id];
    if (MULTI_SELECT_QUESTIONS.has(currentQuestion.id)) {
      return Array.isArray(answer) && answer.length > 0;
    }
    return typeof answer === "string" && answer.trim().length > 0;
  };

  const allAnswered = answeredCount === totalQuestions;

  // Central "go to next question, or submit if this was the last one" helper.
  // Used by both auto-advance (single-select) and the manual Continue button
  // (multi-select).
  const goToNextQuestion = () => {
    setStep((current) => {
      if (current < QUESTIONS.length - 1) {
        return current + 1;
      }
      return current;
    });
  };

  const handleSingleSelect = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Auto-advance only for single-select questions, and only if this isn't
    // the final question — the final question always requires an explicit
    // "Submit Profile" click since submission triggers a real backend call.
    if (step < QUESTIONS.length - 1) {
      window.setTimeout(() => {
        goToNextQuestion();
      }, AUTO_ADVANCE_DELAY);
    }
  };

  const handleMultiSelect = (questionId: string, value: string, checked: boolean) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as string[]) : [];

      if (questionId === "q3") {
        if (value === "All platforms") {
          if (checked) {
            return { ...prev, [questionId]: ["All platforms"] };
          }
          return { ...prev, [questionId]: [] };
        }

        if (checked && current.includes("All platforms")) {
          return { ...prev, [questionId]: current.filter((item) => item !== "All platforms") };
        }
      }

      if (checked) {
        if (current.includes(value)) return prev;
        return { ...prev, [questionId]: [...current, value] };
      }

      return { ...prev, [questionId]: current.filter((item) => item !== value) };
    });
  };

  // Manual "Next"/"Continue" button — used for multi-select questions and as
  // a fallback / for going forward from the review-highlighted state.
  const handleNext = () => {
    if (!isCurrentAnswered()) {
      toast.error("Please answer this question before continuing");
      return;
    }
    if (step < totalQuestions - 1) {
      goToNextQuestion();
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep((s) => s - 1);
    }
  };

  const handleSubmit = async () => {
    if (!allAnswered || isSubmitting) return;

    setIsSubmitting(true);
    setStage("loading");
    setFailureReasons([]);
    setAnalyzingStep(0);

    analyzingTimerRef.current = setInterval(() => {
      setAnalyzingStep((s) => {
        if (s >= analyzingSteps.length - 1) {
          clearInterval(analyzingTimerRef.current as ReturnType<typeof setInterval>);
          return s;
        }
        return s + 1;
      });
    }, 1200);

    await new Promise((resolve) => setTimeout(resolve, 5000));

    const result = validateAnswers(answers);

    if (!result.passed) {
      if (analyzingTimerRef.current) {
        clearInterval(analyzingTimerRef.current);
      }
      setStage("review");
      setIsSubmitting(false);
      setFailureReasons(result.reasons);
      setFailedQuestions(result.failedQuestions);
      toast.error("Verification failed. Please review your answers.");
      return;
    }

    try {
      const response = await fetch("/api/onboarding/complete", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });

      if (!response.ok) {
        throw new Error("Failed to save onboarding");
      }

      const data = await response.json();
      if (data.user) {
        setUser(data.user);
      }

      if (analyzingTimerRef.current) {
        clearInterval(analyzingTimerRef.current);
      }

      localStorage.removeItem("onboarding_answers");
      setStage("success");
      toast.success("Profile verified. You can now start testing.");

      setTimeout(() => {
        navigate({ to: "/" });
      }, 2000);
    } catch (err) {
      if (analyzingTimerRef.current) {
        clearInterval(analyzingTimerRef.current);
      }
      toast.error(err instanceof Error ? err.message : "Failed to complete onboarding");
      setStage("review");
      setIsSubmitting(false);
    }
  };

  if (stage === "loading") {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="rounded-3xl border border-blue-200 bg-white p-8 shadow-sm">
            <div className="flex flex-col items-center mb-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
              <h1 className="text-2xl font-bold text-foreground">Verifying your profile</h1>
              <p className="mt-2 text-sm text-slate-600">
                Please wait while we analyze your responses...
              </p>
            </div>

            <div className="space-y-0">
              {analyzingSteps.map((stepText, index) => {
                const isCompleted = index < analyzingStep;
                const isCurrent = index === analyzingStep;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                          isCompleted
                            ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                            : isCurrent
                              ? "border-blue-500 bg-blue-50 text-blue-600"
                              : "border-slate-200 bg-white text-slate-400"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : isCurrent ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Circle className="h-4 w-4" />
                        )}
                      </div>
                      {index < analyzingSteps.length - 1 && (
                        <div
                          className={`h-8 w-px ${isCompleted ? "bg-emerald-500" : "bg-slate-200"}`}
                        />
                      )}
                    </div>
                    <div className="flex-1 pt-1">
                      <p
                        className={`text-sm font-medium ${
                          isCompleted
                            ? "text-emerald-700"
                            : isCurrent
                              ? "text-blue-700"
                              : "text-slate-500"
                        }`}
                      >
                        {stepText}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (stage === "success") {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="flex flex-col items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 p-8 shadow-sm text-center">
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-600" />
            <h1 className="text-2xl font-bold text-foreground">You're now verified!</h1>
            <p className="mt-2 text-sm text-slate-700">
              You can start accepting app and web tests.
            </p>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen pb-20 md:pb-0">
      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Tester Qualification
            </h1>
            <span className="text-xs text-slate-500">
              {answeredCount} / {totalQuestions} completed
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Before accessing tests, we need to verify your attention to detail and consistency. This
            takes ~few minutes.
          </p>
        </div>

        {failureReasons.length > 0 && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <h3 className="text-sm font-semibold text-red-700">Review Needed</h3>
            </div>
            <ul className="list-disc pl-5 text-sm text-red-600 space-y-1">
              {failureReasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-red-500">
              Please review the highlighted questions below and update your answers.
            </p>

            {failedQuestions.some((f) => f.id === "q5") && (
              <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-800 mb-2">Quick Focus Test</p>
                <p className="text-sm text-blue-700 mb-3">Please confirm your selection:</p>
                <RadioGroup
                  value={typeof answers["q5"] === "string" ? answers["q5"] : ""}
                  onValueChange={(value) => handleSingleSelect("q5", value)}
                >
                  {["Low", "Moderate", "High"].map((opt) => (
                    <div key={opt} className="flex items-center gap-2">
                      <RadioGroupItem value={opt} id={`retry-${opt}`} />
                      <Label htmlFor={`retry-${opt}`} className="text-sm">
                        {opt}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
        )}

        <div className="rounded-3xl border border-blue-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600">
                Question {step + 1} of {totalQuestions}
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="space-y-6">
            <div key={currentQuestion.id} className="space-y-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {step + 1}. {currentQuestion.text}
                </p>
                {failedQuestions.some((f) => f.id === currentQuestion.id) && (
                  <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                    <AlertCircle className="h-3 w-3" />
                    Review needed
                  </span>
                )}
              </div>
              {failedQuestions.find((f) => f.id === currentQuestion.id)?.expected && (
                <p className="text-xs text-red-600 bg-red-100 rounded-lg px-3 py-2">
                  <span className="font-semibold">Expected:</span>{" "}
                  {failedQuestions.find((f) => f.id === currentQuestion.id)?.expected}
                </p>
              )}
              {failedQuestions.find((f) => f.id === currentQuestion.id)?.reason && (
                <p className="text-xs text-red-500">
                  {failedQuestions.find((f) => f.id === currentQuestion.id)?.reason}
                </p>
              )}

              {currentQuestion.multi ? (
                <div className="space-y-2">
                  {currentQuestion.options.map((option) => {
                    const selected =
                      Array.isArray(answers[currentQuestion.id]) &&
                      (answers[currentQuestion.id] as string[]).includes(option);
                    return (
                      <div
                        key={option}
                        className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/40 px-4 py-3 cursor-pointer"
                        onClick={() => handleMultiSelect(currentQuestion.id, option, !selected)}
                      >
                        <Checkbox
                          id={`${currentQuestion.id}-${option}`}
                          checked={!!selected}
                          onCheckedChange={(checked) =>
                            handleMultiSelect(currentQuestion.id, option, checked === true)
                          }
                        />
                        <Label
                          htmlFor={`${currentQuestion.id}-${option}`}
                          className="flex-1 cursor-pointer text-sm font-medium text-slate-700"
                        >
                          {option}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <RadioGroup
                  value={
                    typeof answers[currentQuestion.id] === "string"
                      ? (answers[currentQuestion.id] as string)
                      : ""
                  }
                  onValueChange={(value) => handleSingleSelect(currentQuestion.id, value)}
                >
                  {currentQuestion.options.map((option) => (
                    <div
                      key={option}
                      className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/40 px-4 py-3 cursor-pointer"
                      onClick={() => handleSingleSelect(currentQuestion.id, option)}
                    >
                      <RadioGroupItem value={option} id={`${currentQuestion.id}-${option}`} />
                      <Label
                        htmlFor={`${currentQuestion.id}-${option}`}
                        className="flex-1 cursor-pointer text-sm font-medium text-slate-700"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-between">
            <Button variant="outline" onClick={handleBack} disabled={step === 0} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            {/* For single-select questions, the answer itself auto-advances,
                so this button mainly matters for multi-select questions and
                the final "Submit Profile" step. It stays visible/enabled as
                a fallback in all cases. */}
            <Button
              onClick={handleNext}
              disabled={!isCurrentAnswered() || isSubmitting}
              className="bg-blue-600 hover:bg-green-600 text-white gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : step === totalQuestions - 1 ? (
                "Submit Profile"
              ) : currentQuestion.multi ? (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
